/**
 * Who a Business Discovery is for, and where its link points.
 *
 * Pure, and free of React and of the API client, so the rules can be tested
 * on their own — `consoleData` re-exports them for the pages to use.
 *
 * The question these answer is the one the console could not answer before:
 * a client writes in a week later saying they lost their link, and the
 * operator has to find the right session among several and send the SAME
 * link back. That means identifying sessions by the names the client gave,
 * not by what the conversation happened to reveal — a client who never
 * opened their link revealed nothing at all.
 */

/**
 * The client's assessment link, in full.
 *
 * Built from `assessment_path`, which the internal session record already
 * carries: the link issued when the session was created, not a new one. So
 * sending it again reaches the same conversation, and nothing is created,
 * rotated or spent by reading it.
 *
 * @param {string | null | undefined} path
 * @param {string} [origin] Injected for tests; defaults to this page's origin.
 */
export function clientUrl(path, origin = globalThis.location?.origin ?? '') {
  return path ? `${origin}${path}` : null;
}

/**
 * Which organisation a session belongs to.
 *
 * The session record is the source: it holds what the client typed on the
 * form that opened their link, which is known from that moment and does not
 * depend on the conversation getting anywhere. The discovery state's
 * extracted organisation is a fallback, for sessions recorded before the
 * form existed.
 */
export function companyOf(session, discovery) {
  return session?.organization_name || discovery?.organization || null;
}

/** Who at that organisation this assessment is with. */
export function clientOf(session) {
  return session?.client_name || null;
}

/**
 * True when a session matches what the operator typed into the search box.
 *
 * Matches on the names from the form as well as on what the discovery found,
 * so a link that was issued and never opened is still findable by its own
 * reference.
 */
export function matchesSearch(session, discovery, needle) {
  const query = (needle ?? '').trim().toLowerCase();
  if (!query) return true;
  return [
    session?.session_id,
    session?.organization_name,
    session?.client_name,
    discovery?.organization,
    discovery?.industry,
  ]
    .filter(Boolean)
    .some((text) => String(text).toLowerCase().includes(query));
}
